/**
 * Un aviso de «esto está a medias».
 *
 * Nació como el aviso del estado de Stripe y ahora lo comparten las pantallas
 * que tienen algo a medio rellenar —los datos del negocio, las políticas—,
 * porque son el mismo objeto: un titular, una frase que dice qué falta y, si
 * hay algo que hacer, un botón. Cuando cada pantalla se pintaba el suyo,
 * acababan diciendo lo mismo con distinto aspecto.
 *
 * No bloquea nada. Dice qué falta y, cuando se puede, lleva a donde se arregla.
 *
 * Sin animación propia, salvo cuando se pide con `reveal`. Entra con el resto
 * del contenido de su pantalla, porque desde que la espera cuenta también la
 * consulta del estado, el aviso está ahí desde el primer fotograma y un gesto
 * suyo sólo lo separaba de sus vecinos.
 *
 * `reveal` es para el caso contrario, que existe y es uno: Depósitos. Ahí lo
 * que hay debajo es un marco de Stripe que monta cuando le parece, así que no
 * se puede esperar a que termine para pintar la pantalla — y el aviso sí llega
 * de golpe sobre algo ya dibujado. Ver `.dash-notice-reveal`.
 */
import type { ReactNode } from 'react'
import { IconAlertTriangle } from '@tabler/icons-react'

/**
 * `accent` es el color de la marca del sitio, y no una advertencia.
 *
 * Lo usa lo que está PENDIENTE pero no va mal: una oferta en borrador no es un
 * problema que arreglar, es un paso que falta. En ámbar se leía como un aviso, y
 * el ámbar conviene guardarlo para cuando de verdad hay algo roto.
 */
export type NoticeTone = 'warn' | 'brand' | 'error' | 'accent'

export function Notice({ tone = 'warn', icon, title, children, actions, reveal = false }: {
  tone?: NoticeTone
  /** Por omisión el triángulo. Stripe pone su marca cuando aún no hay cuenta. */
  icon?: ReactNode
  title: string
  children: ReactNode
  actions?: ReactNode
  /**
   * Que entre abriendo su altura, en vez de estar puesto desde el principio.
   *
   * Sólo donde el aviso llega DESPUÉS que la pantalla. En todo lo demás sería
   * un gesto de más: llamar la atención sobre algo que ya estaba.
   */
  reveal?: boolean
}) {
  if (reveal) {
    return (
      /* Dos capas, y las dos hacen falta.
         La de fuera anima `grid-template-rows` de `0fr` a `1fr` —que sí es
         animable— para que lo de abajo se aparte suavemente en vez de dar un
         tirón. La de dentro va vacía porque el relleno y el borde del aviso NO
         colapsan con la fila: siendo él el hijo directo, el pliegue arrancaba
         con treinta píxeles ya hechos y eso era justo lo que se veía abrupto. */
      <div className="dash-notice-reveal">
        <div className="dash-notice-reveal-fold">
          <div className={`dash-notice is-${tone}`}>
            {/* El mismo vidrio que los iconos del resto del panel, teñido con el
                tono del aviso. Era un cuadrado plano con un color al 12%, y al
                lado de cualquier widget se leía como una pieza de otra época. */}
            <span className="dash-notice-icon dash-widget-glass-icon" aria-hidden="true">
              {icon ?? <IconAlertTriangle size={17} />}
            </span>
            <div className="dash-notice-copy">
              <strong>{title}</strong>
              {children}
            </div>
            {actions ? <div className="dash-notice-actions">{actions}</div> : null}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`dash-notice is-${tone}`}>
      {/* El mismo vidrio teñido que en la rama de arriba: dos ramas del mismo
          componente no pueden enseñar dos iconos distintos. */}
      <span className="dash-notice-icon dash-widget-glass-icon" aria-hidden="true">
        {icon ?? <IconAlertTriangle size={17} />}
      </span>

      <div className="dash-notice-copy">
        <strong>{title}</strong>
        {children}
      </div>

      {actions ? <div className="dash-notice-actions">{actions}</div> : null}
    </div>
  )
}
